import {
  getRememberedAssetClass,
  rememberAssetClass,
  setSelectedAssetClass as applySlug,
  setScopedRoutes as persistScopedRoutes,
} from '../../utils/assetClassState'
import { resolveEntryAssetClass } from '../../utils/assetClassEntry'

// Builds a { slug: { activeProducts, totalIssuers } } map from the user/info counters array.
function buildCountersMap(list) {
  const map = {}
  if (Array.isArray(list)) {
    list.forEach((entry) => {
      if (entry && entry.assetClass) {
        map[entry.assetClass] = {
          activeProducts: entry.activeProducts,
          totalIssuers: entry.totalIssuers,
        }
      }
    })
  }
  return map
}

// Holds the asset classes the user can operate on, the currently-selected one, and the per-class
// product counters shown in the selector.
// Writes flow through assetClassState so the fetcher (which can't import the store) stays in sync.
// The selection of a page load comes from its URL; only an explicit choice (selectAssetClass) is
// remembered for the next un-prefixed entry.
export default {
  state: {
    available: [],
    selected: null,
    rejected: null,
    // { slug: { activeProducts, totalIssuers } } — seeded from user/info, refreshed by the search
    // page (see updateAssetClassCounters) so the selector stays aligned without extra round trips.
    counters: {},
  },
  mutations: {
    setAvailableAssetClasses(state, slugs) {
      state.available = Array.isArray(slugs) ? slugs : []
    },
    setSelectedAssetClass(state, slug) {
      state.selected = slug || null
      applySlug(state.selected)
    },
    // The URL prefix the user asked for is a known asset class they cannot use: kept so the boot can
    // tell them why they were moved to another one.
    setRejectedAssetClass(state, slug) {
      state.rejected = slug || null
    },
    setAssetClassCounters(state, countersBySlug) {
      state.counters = countersBySlug && typeof countersBySlug === 'object' ? countersBySlug : {}
    },
    setAssetClassCountersForSlug(state, { slug, activeProducts, totalIssuers }) {
      if (!slug) {
        return
      }
      // Replace the map (not mutate in place) so Vue 2 reactivity picks up new/updated keys.
      state.counters = {
        ...state.counters,
        [slug]: { activeProducts, totalIssuers },
      }
    },
  },
  actions: {
    // Seeds the module from the user/info payload: available list, scoped-route catalog, per-class
    // counters, and the selection. The selection is decided by the entry URL (see
    // resolveEntryAssetClass): its prefix, else — for an un-prefixed URL — IBIPs on resource routes
    // (old links) or the last explicit choice / server default / first on the others.
    initAssetClasses(
      { commit },
      { assetClasses, scopedApiRoutes, defaultAssetClass, assetClassCounters, pathname = window.location.pathname } = {}
    ) {
      const available = Array.isArray(assetClasses) ? assetClasses : []
      commit('setAvailableAssetClasses', available)
      persistScopedRoutes(scopedApiRoutes)
      commit('setAssetClassCounters', buildCountersMap(assetClassCounters))

      const entry = resolveEntryAssetClass({
        pathname,
        available,
        defaultAssetClass,
        remembered: getRememberedAssetClass(),
      })
      commit('setRejectedAssetClass', entry.rejected)
      commit('setSelectedAssetClass', entry.slug)
    },
    // Explicit choice from the selector: applied and remembered as the start of the next un-prefixed URL.
    selectAssetClass({ commit }, slug) {
      commit('setSelectedAssetClass', slug)
      rememberAssetClass(slug)
    },
    // Piggyback: refresh a single asset class entry from a /counters/totals response, keeping the
    // selector aligned with the search page without an extra round trip.
    updateAssetClassCounters({ commit }, { slug, activeProducts, totalIssuers }) {
      commit('setAssetClassCountersForSlug', { slug, activeProducts, totalIssuers })
    },
  },
  getters: {
    getAvailableAssetClasses: (state) => state.available,
    getSelectedAssetClass: (state) => state.selected,
    getRejectedAssetClass: (state) => state.rejected,
    hasMultipleAssetClasses: (state) => state.available.length > 1,
    getAssetClassCounters: (state) => state.counters,
  },
}
